import * as CL from '@design-system/component-library';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { dsClass } from '../../../../common/constants/dsClasses.js';
import { t } from '../../../../common/i18n/index.js';
import { DeliveryOption } from '../../../../generated/api/deliveryOption.js';
import type { DeliveryOptionsResponse } from '../../../../generated/api/deliveryOptionsResponse.js';
import type { PlaceOrderFormData } from '../../placeOrderTypes.js';

import './TotalOrPartialDeliveryButtons.scss';

interface DeliveryOptionSelectionCoCoProps {
  deliveryOptions?: DeliveryOptionsResponse;
}

const DEFAULT_DELIVERY_OPTION = DeliveryOption.TOTAL_DELIVERY;

const DELIVERY_OPTION_ICONS: Record<string, string> = {
  [DeliveryOption.TOTAL_DELIVERY]: 'icon-total-delivery',
  [DeliveryOption.PARTIAL_DELIVERY]: 'icon-partial-delivery',
};

const getDeliveryOptionName = (option: DeliveryOption) =>
  option === DeliveryOption.PARTIAL_DELIVERY
    ? t.Q2QT('Allow partial delivery')
    : t.EADV('Deliver all products in one batch');

export const DeliveryOptionSelectionCoCo = ({ deliveryOptions }: DeliveryOptionSelectionCoCoProps) => {
  const { setValue, watch } = useFormContext<PlaceOrderFormData>();
  const shipmentType = watch('delivery.shipmentType');

  const availableOptions = deliveryOptions?.deliveryOptions ?? [];
  const soleOption = availableOptions.length === 1 ? availableOptions[0] : undefined;
  const isSelectionAvailable = availableOptions.length > 1;

  // Preselect the sole option, or the default resolved for this order, until the user picks one.
  useEffect(() => {
    if (availableOptions.length === 0 || shipmentType) {
      return;
    }
    const preselected = soleOption ?? deliveryOptions?.defaultDeliveryOption ?? DEFAULT_DELIVERY_OPTION;
    setValue('delivery.shipmentType', preselected);
  }, [availableOptions.length, soleOption, deliveryOptions?.defaultDeliveryOption, shipmentType, setValue]);

  if (!isSelectionAvailable) {
    return null;
  }

  return (
    <div className="of-place-order-shipment-type">
      <p className={dsClass.FONT_WEIGHT_BOLD}>{t.AKD6('Select delivery of products')}</p>
      <CL.Disclaimer
        className={dsClass.MARGIN_BOTTOM_4}
        disclaimerType="info"
        icon={<CL.Icon icon="information" />}
        text={t.UQPC(
          'Your order contains products that are not available right now. Choose whether you want devices in partial delivery or all at once.'
        )}
        visible
      />
      <CL.SelectGroup
        className={`of-place-order-shipment-type__list ${dsClass.MARGIN_BOTTOM_3}`}
        products={availableOptions.map(option => ({
          id: option,
          name: getDeliveryOptionName(option),
          icon: (
            <span className={`of-place-order-shipment-type__list--icon icon ${DELIVERY_OPTION_ICONS[option] ?? ''}`} />
          ),
        }))}
        value={shipmentType}
        onChange={(id: string) => setValue('delivery.shipmentType', id as DeliveryOption)}
      />
      {shipmentType === DeliveryOption.TOTAL_DELIVERY && (
        <p className={dsClass.TEXT_S}>{t.MPKA('Your order will be shipped as soon as all products are available.')}</p>
      )}
      {shipmentType === DeliveryOption.PARTIAL_DELIVERY && (
        <p className={dsClass.TEXT_S}>
          {t.DQDK('Products will be delivered as soon as they are available. Delivery fee will be charged only once.')}
        </p>
      )}
    </div>
  );
};
